import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Calendar, Clock, User, Mail, Phone, CheckCircle, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { trackFormSubmit } from '../utils/analytics';
import { BACKEND_URL } from '../lib/apiBase';

// ============================================================================
// TARTALEK-LISTA — NE TOROLD.
//
// A kepzesek es idopontok 2026-08-27 ota az adatbazisbol jonnek
// (GET /api/trainings), es a dhakademia.hu/admin feluletrol szerkesztheto.
//
// Ez a lista akkor lep eletbe, ha az API barmiert nem valaszol. Igy egy
// szerver-hiba legrosszabb kovetkezmenye egy ELAVULT DATUM, nem pedig egy
// halott jelentkezesi urlap. Ezert marad itt, es ezert frissitendo evente.
// ============================================================================
const TARTALEK_TRAININGS = [
  {
    id: 'stresszkezeles',
    name: 'Stresszkezelés',
    category: 'workshop',
    dates: [],
  },
  {
    id: 'targyalastechnika',
    name: 'Tárgyalástechnika',
    category: 'workshop',
    dates: [
      { date: '2026-09-14', time: '09:00-16:00' },
    ],
  },
  {
    id: 'asszertiv-kommunikacio',
    name: 'Asszertív kommunikáció',
    category: 'workshop',
    dates: [
      { date: '2026-08-05', time: '09:00-16:00' },
    ],
  },
  {
    id: 'tranzakcioanalizis',
    name: 'Tranzakcióanalízis',
    category: 'workshop',
    dates: [],
  },
  {
    id: 'disc',
    name: 'DISC személyiségtípusok',
    category: 'workshop',
    dates: [
      { date: '2026-08-12', time: '09:00-16:00' },
      { date: '2026-09-03', time: '09:00-16:00' },
    ],
  },
  {
    id: 'vevőkezeles',
    name: 'Vevőkezelés',
    category: 'workshop',
    dates: [
      { date: '2026-09-10', time: '09:00-16:00' },
    ],
  },
  {
    id: 'kommunikacio-mesterfokon',
    name: 'Kommunikáció mesterfokon',
    category: 'workshop',
    dates: [
      { date: '2026-08-05', time: '09:00-16:00' },
    ],
  },
  {
    id: 'komfortzonan-tul',
    name: 'Komfortzónán túl – Közelebb Önmagadhoz',
    category: 'workshop',
    dates: [
      { date: '2026-08-13', time: '09:00-16:00' },
    ],
  },
  {
    id: 'ingatlan-nappali',
    name: 'Ingatlanközvetítői képzés - Nappali',
    subtitle: 'Kecskeméti SZC Fazekas István Szakiskola',
    category: 'kepzes',
    dates: [],
  },
  {
    id: 'ingatlan-esti',
    name: 'Ingatlanközvetítői képzés - Esti',
    subtitle: 'BKSZC Szamos Mátyás Technikum és Szakképző Iskola',
    category: 'kepzes',
    dates: [],
  },
];

// A mai nap ISO-formatumban, HELYI ido szerint (a szerver is jelol, ez a ketto egyutt ved).
const maiNap = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const RegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [trainings, setTrainings] = useState(TARTALEK_TRAININGS);

  // A kepzesek betoltese az adatbazisbol. Ha barmi hiba van, marad a tartalek-lista.
  useEffect(() => {
    let ervenyes = true;
    axios
      .get(`${BACKEND_URL}/api/trainings`)
      .then((res) => {
        if (!ervenyes) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setTrainings(res.data);
        }
      })
      .catch(() => {
        /* szandekosan nema: marad a TARTALEK_TRAININGS */
      });
    return () => {
      ervenyes = false;
    };
  }, []);

  // A jelentkezonek csak a MEG NEM LEJART idopontokat kinaljuk fel.
  const elerhetoTrainings = useMemo(() => {
    const ma = maiNap();
    return trainings.map((t) => ({
      ...t,
      dates: (t.dates || []).filter((d) => !d.is_past && d.date >= ma),
    }));
  }, [trainings]);

  useEffect(() => {
    const kepzesId = searchParams.get('kepzes');
    // A kivalasztott kepzest frissiteni kell, amikor megjonnek az adatbazisbol az idopontok.
    const azonosito = selectedTraining ? selectedTraining.id : kepzesId;
    if (!azonosito) return;
    const found = elerhetoTrainings.find((t) => t.id === azonosito);
    if (!found) return;
    setSelectedTraining(found);
    if (!selectedTraining) setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, elerhetoTrainings]);

  const handleTrainingSelect = (training) => {
    setSelectedTraining(training);
    setSelectedDate(null);
    setIsDropdownOpen(false);
    setStep(2);
  };

  const handleDropdownToggle = () => {
    if (!isDropdownOpen) {
      setSelectedDate(null);
      setStep(1);
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDateSelect = (dateInfo) => {
    setSelectedDate(dateInfo);
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTraining || !selectedDate) {
      toast.error('Kerem, valassz kepzest es idopontot.');
      return;
    }
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/registrations`, {
        ...formData,
        training_id: selectedTraining.id,
        training_name: selectedTraining.name,
        date: selectedDate.date,
        time: selectedDate.time,
      });
      trackFormSubmit('registration', { training_id: selectedTraining.id, training_name: selectedTraining.name });
      setSuccess(true);
      toast.success('Jelentkezés sikeres!');
    } catch (error) {
      toast.error('Hiba történt a jelentkezés során. Kérjük, próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.name && formData.email && formData.phone;

  if (success) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-20" data-testid="registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-brand-dark-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-brand-dark-green" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            Jelentkezés sikeres!
          </h1>
          <p className="text-white/80 font-medium mb-2">
            {selectedTraining.name}
          </p>
          <p className="text-white/60 mb-2">
            Időpont: {format(new Date(selectedDate.date), 'yyyy. MMMM d.', { locale: hu })}{selectedDate.endDate && ` – ${format(new Date(selectedDate.endDate), 'yyyy. MMMM d.', { locale: hu })}`}
          </p>
          <p className="text-white/60 mb-8">
            {selectedDate.time}
          </p>
          <p className="text-white/50 text-sm mb-8">
            Hamarosan küldünk egy megerősítő emailt a megadott címre a további részletekkel.
          </p>
          <a href="/" className="btn-outline inline-block">
            Vissza a főoldalra
          </a>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-32 pb-20" data-testid="registration-page">
      <SEO
        title="Jelentkezés"
        description="Jelentkezz képzéseinkre: ingatlanközvetítői képzés, workshopok, tréningek. Válassz képzést és időpontot online."
        path="/jelentkezes"
      />
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <p className="text-brand-light-green font-mono text-sm tracking-widest uppercase mb-4">
            Jelentkezés
          </p>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-semibold text-white mb-4">
            Jelentkezz képzéseinkre
          </h1>
          <p className="text-white/60 text-lg">
            Válaszd ki a képzést, majd az időpontot, és add meg az adataidat.
          </p>
        </motion.div>

        {/* Steps indicator */}
        <div className="flex justify-center gap-2 md:gap-4 mb-8 md:mb-12">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-brand-light-green' : 'text-white/30'}`}>
            <div className={`w-7 h-7 md:w-8 md:h-8 text-sm rounded-full flex items-center justify-center ${step >= 1 ? 'bg-brand-light-green text-black' : 'bg-white/10'}`}>
              1
            </div>
            <span className="hidden sm:inline text-sm">Képzés választás</span>
          </div>
          <div className="w-8 md:w-12 h-[2px] bg-white/10 self-center" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-brand-light-green' : 'text-white/30'}`}>
            <div className={`w-7 h-7 md:w-8 md:h-8 text-sm rounded-full flex items-center justify-center ${step >= 2 ? 'bg-brand-light-green text-black' : 'bg-white/10'}`}>
              2
            </div>
            <span className="hidden sm:inline text-sm">Időpont</span>
          </div>
          <div className="w-8 md:w-12 h-[2px] bg-white/10 self-center" />
          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-brand-light-green' : 'text-white/30'}`}>
            <div className={`w-7 h-7 md:w-8 md:h-8 text-sm rounded-full flex items-center justify-center ${step >= 3 ? 'bg-brand-light-green text-black' : 'bg-white/10'}`}>
              3
            </div>
            <span className="hidden sm:inline text-sm">Adatok</span>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Step 1: Training Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 md:p-8 mb-6"
          >
            <h3 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-brand-light-green" />
              Válassz képzést
            </h3>
            
            {/* Custom Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={handleDropdownToggle}
                className="w-full p-4 bg-white/5 border border-white/10 text-left flex items-center justify-between hover:border-brand-light-green/50 transition-colors"
                data-testid="training-dropdown"
              >
                <span className={selectedTraining ? 'text-white' : 'text-white/50'}>
                  {selectedTraining ? selectedTraining.name : 'Válassz képzést...'}
                </span>
                <ChevronDown size={20} className={`text-white/50 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute z-20 w-full mt-2 bg-[#0a3d4f] border border-white/10 shadow-xl max-h-80 overflow-y-auto"
                >
                  <div className="p-2">
                    <p className="text-white/40 text-xs uppercase tracking-wider px-3 py-2">Workshopok & Tréningek</p>
                    {elerhetoTrainings.filter(t => t.category === 'workshop').map((training) => (
                      <button
                        key={training.id}
                        onClick={() => handleTrainingSelect(training)}
                        className="w-full text-left px-3 py-3 text-white hover:bg-brand-light-green/20 transition-colors"
                        data-testid={`training-option-${training.id}`}
                      >
                        {training.name}
                      </button>
                    ))}
                    <div className="border-t border-white/10 my-2" />
                    <p className="text-white/40 text-xs uppercase tracking-wider px-3 py-2">Szakmai képzés</p>
                    {elerhetoTrainings.filter(t => t.category === 'kepzes').map((training) => (
                      <button
                        key={training.id}
                        onClick={() => handleTrainingSelect(training)}
                        className="w-full text-left px-3 py-3 text-white hover:bg-brand-light-green/20 transition-colors"
                        data-testid={`training-option-${training.id}`}
                      >
                        <span className="block">{training.name}</span>
                        {training.subtitle && (
                          <span className="block text-white/50 text-sm">{training.subtitle}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Step 2: Date Selection */}
          {step >= 2 && selectedTraining && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 md:p-8 mb-6"
            >
              <h3 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
                <Clock size={20} className="text-brand-light-green" />
                Válassz időpontot
              </h3>
              <p className="text-white/60 text-sm mb-4">
                {selectedTraining.name} - elérhető időpontok:
              </p>
              
              {selectedTraining.dates.length === 0 ? (
                <div className="p-6 bg-white/5 border border-white/10 text-center">
                  <p className="text-white/60 mb-2">Jelenleg nincs elérhető időpont ehhez a képzéshez.</p>
                  <p className="text-white/40 text-sm">Kérjük, vedd fel velünk a kapcsolatot az aktuális indulási időpontokért.</p>
                  <a href="/#kapcsolat" className="btn-outline inline-block mt-4 text-sm">
                    Kapcsolatfelvétel
                  </a>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedTraining.dates.map((dateInfo, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDateSelect(dateInfo)}
                      className={`w-full p-4 text-left transition-all flex items-center justify-between ${
                        selectedDate?.date === dateInfo.date
                          ? 'bg-brand-light-green/20 border border-brand-light-green'
                          : 'bg-white/5 border border-white/10 hover:border-brand-light-green/50'
                      }`}
                      data-testid={`date-option-${idx}`}
                    >
                      <div>
                        <p className="text-white font-medium">
                          {format(new Date(dateInfo.date), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
                          {dateInfo.endDate && ` – ${format(new Date(dateInfo.endDate), 'yyyy. MMMM d.', { locale: hu })}`}
                        </p>
                        <p className="text-white/60 text-sm">{dateInfo.time}</p>
                      </div>
                      <div className="text-right">
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Form */}
          {step >= 3 && selectedDate && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Selected summary */}
              <div className="glass-card p-6 mb-6">
                <h3 className="font-heading text-lg text-white mb-3">Kiválasztott képzés</h3>
                <div className="flex flex-wrap gap-4 text-white/70 text-sm">
                  <span className="flex items-center gap-2">
                    <Calendar size={16} className="text-brand-light-green" />
                    {selectedTraining.name}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={16} className="text-brand-light-green" />
                    {format(new Date(selectedDate.date), 'yyyy. MMMM d.', { locale: hu })}{selectedDate.endDate && ` – ${format(new Date(selectedDate.endDate), 'yyyy. MMMM d.', { locale: hu })}`} - {selectedDate.time}
                  </span>
                </div>
                <button
                  onClick={() => { setStep(1); setSelectedTraining(null); setSelectedDate(null); }}
                  className="text-brand-light-green text-sm mt-3 hover:underline"
                >
                  Módosítás
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="glass-card p-6 md:p-8" data-testid="registration-form">
                <h3 className="font-heading text-xl text-white mb-6">Adataid</h3>
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <User size={18} className="text-white/30 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Teljes név *"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-underline"
                      data-testid="registration-input-name"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-white/30 flex-shrink-0" />
                    <input
                      type="email"
                      placeholder="Email cím *"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-underline"
                      data-testid="registration-input-email"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-white/30 flex-shrink-0" />
                    <input
                      type="tel"
                      placeholder="Telefonszám *"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-underline"
                      data-testid="registration-input-phone"
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="Megjegyzés (opcionális)"
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="input-underline resize-none"
                      data-testid="registration-input-notes"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !canSubmit}
                    className="btn-primary w-full disabled:opacity-50"
                    data-testid="registration-submit-btn"
                  >
                    {loading ? 'Jelentkezés...' : 'Jelentkezem'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </div>
    </main>
  );
};

export default RegistrationPage;
